import React, { useState, useEffect } from 'react';
import {
  Heart, ShoppingCart, Trash2, ArrowLeft, Package, Sparkles,
  Star, ShoppingBag, ChevronRight, TrendingUp, ArrowRight
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { wishlistService } from '../api/wishlist';
import { cartService } from '../api/cart';
import { formatINR, calculateDiscount } from '../utils/currency';
import { useCart } from '../context/CartContext';
import { API_URL } from '../api';
import { usePricingRefresh } from '../hooks/usePricingRefresh';
import { getProductSellingPrice } from '../utils/pricing';

/* ============================================================
   ANIMATION VARIANTS
   ============================================================ */
const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1, scale: 1,
    transition: { duration: 0.4 }
  }
};

const cardExit = {
  opacity: 0, scale: 0.92, y: -20,
  transition: { duration: 0.3 }
};

/* ============================================================
   TYPES
   ============================================================ */
interface WishlistItem {
  _id: string;
  productId: string;
  name: string;
  slug: string;
  images: { url: string; alt: string }[];
  price: {
    selling: number;
    mrp: number;
    currency: string;
  };
  brand?: string;
  rating?: number;
  reviewCount?: number;
  inStock?: boolean;
}

/* ============================================================
   HERO SECTION
   ============================================================ */
const HeroSection = ({ itemCount }: { itemCount: number }) => (
  <div className="relative bg-gray-900 overflow-hidden">
    {/* Animated gradient orbs */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-20"
        style={{ background: 'radial-gradient(circle, #a78bfa 0%, transparent 70%)', animation: 'floatOrb 6s ease-in-out infinite' }}
      />
      <div
        className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full opacity-15"
        style={{ background: 'radial-gradient(circle, #38bdf8 0%, transparent 70%)', animation: 'floatOrb 8s ease-in-out infinite 2s' }}
      />
      <div
        className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, #818cf8 0%, transparent 70%)', animation: 'floatOrb 7s ease-in-out infinite 1s' }}
      />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <motion.div
        initial="hidden" animate="visible" variants={staggerContainer}
        className="text-center"
      >
        {/* Breadcrumb */}
        <motion.div variants={fadeInUp} className="flex items-center justify-center gap-2 text-sm text-white/50 mb-5">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-white font-medium">My Wishlist</span>
        </motion.div>

        {/* Title */}
        <motion.div variants={fadeInUp} className="flex items-center justify-center gap-3 mb-3">
          <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
            <Heart className="w-6 h-6 text-rose-400" fill="currentColor" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">
            My Wishlist
          </h1>
        </motion.div>

        <motion.p variants={fadeInUp} className="text-white/60 max-w-md mx-auto mb-4">
          {itemCount === 0
            ? 'Save your favorite items and shop them anytime'
            : `${itemCount} ${itemCount === 1 ? 'item' : 'items'} you love — ready to make them yours?`}
        </motion.p>
      </motion.div>
    </div>

    <style>{`
      @keyframes floatOrb {
        0%, 100% { transform: translate(0, 0) scale(1); }
        33% { transform: translate(20px, -15px) scale(1.05); }
        66% { transform: translate(-10px, 10px) scale(0.97); }
      }
    `}</style>
  </div>
);

/* ============================================================
   LOADING SKELETON
   ============================================================ */
const SkeletonCard = () => (
  <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100/60 overflow-hidden shadow-sm">
    <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse" />
    <div className="p-4 space-y-3">
      <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
      <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
      <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse" />
      <div className="flex items-baseline gap-2 mt-2">
        <div className="h-5 w-20 bg-gray-300 rounded animate-pulse" />
        <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="h-10 w-full bg-gray-200 rounded-xl animate-pulse" />
    </div>
  </div>
);

/* ============================================================
   EMPTY STATE
   ============================================================ */
const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
    className="max-w-lg mx-auto text-center py-16 md:py-24"
  >
    {/* Animated Heart */}
    <div className="relative w-32 h-32 mx-auto mb-8">
      <div className="absolute inset-0 bg-rose-100 rounded-full animate-ping opacity-25" />
      <div className="relative w-32 h-32 bg-gradient-to-br from-rose-50 to-pink-100 rounded-full flex items-center justify-center border border-rose-100">
        <motion.div
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Heart className="w-14 h-14 text-rose-400" fill="currentColor" />
        </motion.div>
        <motion.div
          className="absolute -top-1 -right-1"
          animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 2.5, repeat: Infinity }}
        >
          <Sparkles className="w-6 h-6 text-amber-400" />
        </motion.div>
        <motion.div
          className="absolute -bottom-1 -left-1"
          animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, delay: 0.5 }}
        >
          <Sparkles className="w-5 h-5 text-indigo-400" />
        </motion.div>
      </div>
    </div>

    <h2 className="text-2xl font-bold text-gray-900 mb-3">
      Your wishlist is empty
    </h2>
    <p className="text-gray-500 mb-8 max-w-sm mx-auto leading-relaxed">
      Explore our curated collection and save products you love. They will be waiting right here.
    </p>

    <Link
      to="/"
      className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-rose-500 to-pink-600 text-white font-semibold rounded-xl hover:from-rose-600 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl shadow-rose-200/50 hover:scale-[1.03] active:scale-95"
    >
      <ShoppingBag className="w-5 h-5" />
      Start Exploring
      <ArrowRight className="w-4 h-4" />
    </Link>
  </motion.div>
);

/* ============================================================
   WISHLIST CARD
   ============================================================ */
const WishlistCard = ({
  item, onRemove, onAddToCart, isRemoving, isAdding
}: {
  item: WishlistItem;
  onRemove: (id: string) => void;
  onAddToCart: (item: WishlistItem) => void;
  isRemoving: boolean;
  isAdding: boolean;
}) => {
  const discount = calculateDiscount(item.price.mrp, item.price.selling);

  const getImageUrl = (images: any[]) => {
    if (!images || images.length === 0) {
      return 'https://placehold.co/300x400/f0f0f0/999?text=No+Image';
    }
    const url = images[0].url || images[0];
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  return (
    <motion.div
      layout
      variants={scaleIn}
      exit={cardExit}
      className="group bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100/80 overflow-hidden shadow-sm hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-500 relative"
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        <Link to={`/product/${item.slug || item._id}`}>
          <motion.img
            src={getImageUrl(item.images)}
            alt={item.name}
            className="w-full h-full object-contain p-5 transition-transform duration-700 group-hover:scale-110"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://placehold.co/300x300/f0f0f0/999?text=No+Image';
            }}
          />
        </Link>

        {/* Discount Badge */}
        {discount > 0 && (
          <div className="absolute top-3 left-3 bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-lg shadow-lg shadow-emerald-200/40">
            {discount}% OFF
          </div>
        )}

        {/* Remove Button */}
        <button
          onClick={() => onRemove(item._id)}
          disabled={isRemoving}
          title="Remove from wishlist"
          className="absolute top-3 right-3 p-2.5 bg-white/90 backdrop-blur-sm rounded-xl shadow-sm hover:bg-red-50 hover:text-red-500 hover:shadow-md transition-all duration-300 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0"
        >
          <Trash2 className={`w-4 h-4 ${isRemoving ? 'animate-pulse' : ''}`} />
        </button>

        {/* Out of Stock Overlay */}
        {!item.inStock && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center">
            <span className="bg-gray-900/90 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg">
              Out of Stock
            </span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4 space-y-2.5">
        {/* Brand */}
        {item.brand && (
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest truncate">
            {item.brand}
          </p>
        )}

        {/* Product Name */}
        <Link to={`/product/${item.slug || item._id}`}>
          <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 min-h-[2.5rem] hover:text-rose-600 transition-colors duration-300">
            {item.name}
          </h3>
        </Link>

        {/* Rating */}
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-1 bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-md">
            <span className="text-xs font-bold">{typeof item.rating === 'number' ? item.rating.toFixed(1) : '4.0'}</span>
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
          </div>
          {item.reviewCount ? (
            <span className="text-[11px] text-gray-400">({item.reviewCount} reviews)</span>
          ) : (
            <span className="text-[11px] text-gray-300">No reviews</span>
          )}
        </div>

        {/* Price Section */}
        <div className="flex items-baseline gap-2 pt-1">
          <span className="text-lg font-bold text-gray-900">
            {formatINR(item.price.selling)}
          </span>
          {item.price.mrp > item.price.selling && (
            <span className="text-sm text-gray-400 line-through">
              {formatINR(item.price.mrp)}
            </span>
          )}
        </div>

        {/* Action Button */}
        <button
          onClick={() => onAddToCart(item)}
          disabled={isAdding || !item.inStock}
          className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-semibold text-sm transition-all duration-300 ${
            item.inStock
              ? 'bg-gray-900 hover:bg-gray-800 text-white shadow-lg shadow-gray-900/20 hover:shadow-xl hover:shadow-gray-900/30 hover:-translate-y-0.5 active:scale-[0.98]'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isAdding ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <ShoppingCart className="w-4 h-4" />
              {item.inStock ? 'Add to Cart' : 'Out of Stock'}
            </>
          )}
        </button>

        {/* Mobile Remove */}
        <button
          onClick={() => onRemove(item._id)}
          disabled={isRemoving}
          className="sm:hidden w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl font-medium text-sm text-red-500 hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Remove
        </button>
      </div>
    </motion.div>
  );
};

/* ============================================================
   STATS BAR
   ============================================================ */
const StatsBar = ({
  itemCount, totalValue, avgDiscount
}: {
  itemCount: number;
  totalValue: number;
  avgDiscount: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: 0.2 }}
    className="mb-8 grid grid-cols-3 gap-3 sm:gap-4"
  >
    {[
      { label: 'Items Saved', value: itemCount.toString(), icon: Heart },
      { label: 'Total Value', value: formatINR(totalValue), icon: TrendingUp },
      { label: 'Avg. Discount', value: `${avgDiscount}%`, icon: Sparkles },
    ].map((stat, i) => (
      <div
        key={i}
        className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100/80 p-3 sm:p-4 text-center shadow-sm hover:shadow-md transition-shadow"
      >
        <stat.icon className="w-4 h-4 text-rose-400 mx-auto mb-1.5" />
        <div className="text-lg sm:text-xl font-bold text-gray-900">{stat.value}</div>
        <div className="text-[10px] sm:text-xs text-gray-400 font-medium">{stat.label}</div>
      </div>
    ))}
  </motion.div>
);

/* ============================================================
   MAIN PAGE
   ============================================================ */
const WishlistPage = () => {
  const pricingRefresh = usePricingRefresh();
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { addToCart: addToCartContext } = useCart();

  useEffect(() => {
    fetchWishlist();
  }, [pricingRefresh]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await wishlistService.getWishlist();

      if (response.success && response.data) {
        const items = Array.isArray(response.data) ? response.data : [];
        const transformedItems = items.map((item: any) => {
          const p = item.productId && typeof item.productId === 'object' ? item.productId : item;
          const selling = getProductSellingPrice(p);
          return {
          _id: p._id || item._id,
          productId: p._id || item._id,
          name: p.name || 'Product',
          slug: p.slug || '',
          images: p.images || [],
          price: {
            selling,
            mrp: p.price?.mrp ?? p.mrp ?? selling,
            currency: 'INR',
          },
          brand: p.brand || '',
          rating: p.rating?.average ?? p.rating ?? 4,
          reviewCount: p.rating?.count ?? 0,
          inStock: p.inventory?.stock !== 0,
        };
        });
        setWishlistItems(transformedItems);
      } else {
        setWishlistItems([]);
      }
      setError(null);
    } catch (err: any) {
      if (err.message?.includes('Authorization') || err.message?.includes('401')) {
        setWishlistItems([]);
        setError(null);
      } else {
        console.error('Wishlist loading error:', err);
        setError('Unable to load wishlist. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId: string) => {
    try {
      setRemovingId(productId);
      await wishlistService.removeFromWishlist(productId);
      setWishlistItems(prev => prev.filter(item => item._id !== productId));
    } catch (err: any) {
      setError(err.message || 'Failed to remove item');
    } finally {
      setRemovingId(null);
    }
  };

  const addToCart = async (item: WishlistItem) => {
    try {
      setAddingToCart(item._id);
      const token = localStorage.getItem('token');

      if (!token) {
        // ProtectedRoute handles this, but keep as fallback
        console.error('No token found');
        return;
      }

      // Backend sync
      await cartService.addToCart(item.productId || item._id, 1);

      // Frontend sync — update CartContext & localStorage
      addToCartContext({
        productId: item.productId || item._id,
        name: item.name,
        price: item.price.selling,
        mrp: item.price.mrp,
        image: item.images?.[0]?.url || '',
        quantity: 1,
      });

      // Remove from wishlist after adding to cart
      await wishlistService.removeFromWishlist(item._id);
      setWishlistItems(prev => prev.filter(wItem => wItem._id !== item._id));

      navigate('/cart');
    } catch (err: any) {
      setError(err.message || 'Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  const totalValue = wishlistItems.reduce((sum, item) => sum + item.price.selling, 0);
  const avgDiscount = wishlistItems.length > 0
    ? Math.round(
        wishlistItems.reduce((sum, item) => sum + calculateDiscount(item.price.mrp, item.price.selling), 0)
        / wishlistItems.length
      )
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero */}
      <HeroSection itemCount={wishlistItems.length} />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6"
        >
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </motion.div>

        {error ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 md:p-12 text-center max-w-lg mx-auto"
          >
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Oops!</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button
              onClick={fetchWishlist}
              className="px-6 py-2.5 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-colors shadow-lg shadow-gray-900/20"
            >
              Try Again
            </button>
          </motion.div>
        ) : loading ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)}
            </div>
          </>
        ) : wishlistItems.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Stats */}
            <StatsBar
              itemCount={wishlistItems.length}
              totalValue={totalValue}
              avgDiscount={avgDiscount}
            />

            {/* Mobile: Continue Shopping */}
            <Link
              to="/"
              className="sm:hidden flex items-center gap-2 mb-4 text-sm font-medium text-rose-600 hover:text-rose-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Continue Shopping
            </Link>

            {/* Wishlist Grid */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5"
            >
              <AnimatePresence>
                {wishlistItems.map((item) => (
                  <WishlistCard
                    key={item._id}
                    item={item}
                    onRemove={removeFromWishlist}
                    onAddToCart={addToCart}
                    isRemoving={removingId === item._id}
                    isAdding={addingToCart === item._id}
                  />
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Bottom CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-12 text-center"
            >
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-100 rounded-2xl px-6 py-4">
                <ShoppingBag className="w-5 h-5 text-rose-500" />
                <span className="text-gray-700 text-sm">
                  Want to save more? <Link to="/" className="font-semibold text-rose-600 hover:text-rose-700 transition-colors">Browse trending products</Link>
                </span>
                <ArrowRight className="w-4 h-4 text-rose-400" />
              </div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;